import { useEffect, useRef, type ReactNode } from 'react';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { Icono, type NombreIcono } from './Iconos.js';

interface Props {
  /*
    EL DIALOGO NECESITA EL IDIOMA PARA UNA SOLA PALABRA: la del boton de
    cerrar. Es la unica que el componente escribe por su cuenta —el resto se lo
    dan hecho—, y estaba en castellano en las tres versiones de la aplicacion.
    Una etiqueta que solo oye quien usa un lector de pantalla es justo la que
    nadie mira al traducir.
  */
  idioma: Idioma;
  titulo: string;
  icono?: NombreIcono;
  onCerrar: () => void;
  children: ReactNode;
  /**
   * `hoja` lo saca desde abajo y a todo el ancho, como una hoja de accion de
   * movil, en vez de centrado. Para lo que se abre desde la barra inferior: un
   * dialogo en el centro de la pantalla, disparado por un boton que esta pegado
   * al borde de abajo, se siente venido de otro sitio.
   *
   * `amplia` es para una ficha con su propio menú lateral, como la de una
   * oportunidad: más ancha y de alto fijo, para que el menú no salte de sitio
   * al cambiar de sección.
   */
  variante?: 'centrado' | 'hoja' | 'amplia';
}

/**
 * Diálogo modal.
 *
 * Usa `<dialog>` nativo, que es lo que da gratis lo que suele hacerse mal a
 * mano: el foco queda atrapado dentro, `Escape` cierra, y el resto de la
 * página queda inerte para quien navega con lector de pantalla.
 */
export function Modal({
  idioma,
  titulo,
  icono,
  onCerrar,
  children,
  variante = 'centrado',
}: Props) {
  const dialogo = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = dialogo.current;
    if (d && !d.open) d.showModal();
    return () => {
      if (d?.open) d.close();
    };
  }, []);

  return (
    <dialog
      ref={dialogo}
      className={
        variante === 'hoja' ? 'modal modal-hoja' : variante === 'amplia' ? 'modal modal-amplia' : 'modal'
      }
      onCancel={(e) => {
        e.preventDefault();
        onCerrar();
      }}
      // Pulsar el fondo cierra: el fondo es el propio `dialog`, y el contenido
      // detiene la propagación.
      onClick={onCerrar}
    >
      <div className="modal-caja" onClick={(e) => e.stopPropagation()}>
        <div className="modal-cabeza">
          <h2>
            {icono && (
              <span aria-hidden="true" className="modal-icono">
                <Icono nombre={icono} />
              </span>
            )}
            {titulo}
          </h2>
          <button type="button" className="modal-cerrar" aria-label={t(idioma, 'tarea.cerrar')} onClick={onCerrar}>
            <Icono nombre="cerrar" />
          </button>
        </div>
        <div className="modal-cuerpo">{children}</div>
      </div>
    </dialog>
  );
}
